import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { queryKeys } from '@splitfiesta/core';
import type {
  RecurringRuleDto,
  RecurringRulesResponseDto,
  SuccessResponseDto,
  UpdateRecurringRuleDto,
} from '@splitfiesta/shared';

import type { ApiError } from '@/hooks/useApi';
import { useTypedApi } from '@/hooks/useTypedApi';

export function useRecurringRulesQuery(groupId: string, enabled: boolean) {
  const api = useTypedApi();

  return useQuery({
    queryKey: queryKeys.recurringRules(groupId),
    queryFn: async () => {
      const res = (await api.recurringRules.list(
        groupId
      )) as RecurringRulesResponseDto;
      return res.data;
    },
    enabled: enabled && !!groupId,
  });
}

export function useUpdateRecurringRuleMutation(groupId: string) {
  const api = useTypedApi();
  const queryClient = useQueryClient();

  return useMutation<
    RecurringRuleDto,
    ApiError,
    { ruleId: string } & UpdateRecurringRuleDto
  >({
    mutationFn: async ({ ruleId, status }) => {
      const res = (await api.recurringRules.updateStatus(groupId, ruleId, {
        status,
      })) as SuccessResponseDto<RecurringRuleDto>;
      return res.data;
    },
    onSettled: () =>
      queryClient.invalidateQueries({
        queryKey: queryKeys.recurringRules(groupId),
      }),
  });
}

export function useStopRecurringRuleMutation(groupId: string) {
  const api = useTypedApi();
  const queryClient = useQueryClient();

  return useMutation<unknown, ApiError, string>({
    mutationFn: ruleId => api.recurringRules.stop(groupId, ruleId),
    onSettled: () =>
      queryClient.invalidateQueries({
        queryKey: queryKeys.recurringRules(groupId),
      }),
  });
}
